import { createContext, useContext, useEffect } from "react";
import {
  AMP_AGENT_CONTEXT,
  type AmpAgentContext,
  type CanvasContext,
  type LibraryContext,
} from "@hightouch/lib/agent-harness/shared/context-schema";
import { formatV1FocusedContent } from "src/components/llm/agent-focus/serialize-agent-context";
import { useStableWhileEqual } from "src/hooks/use-stable-while-equal";

export const AGENT_CONTEXT = {
  ...AMP_AGENT_CONTEXT,
  LEGACY_V1_EDITOR: "legacyV1Editor",
} as const;

/** V1-only namespaces have explicit owners and never enter a v2 message. */
export type V1LegacyContextValues = {
  legacyV1Editor: Record<string, unknown>;
};
/** Each namespace has one publisher that supplies its complete value. */
export type AgentContextValues = Required<AmpAgentContext> &
  V1LegacyContextValues;
export type AgentContext = Partial<AgentContextValues>;
export type LibraryAgentFocus = LibraryContext;
export type AgentFocusFile = CanvasContext["selectedFiles"][number];

export type AgentFocusContextValue = {
  context: AgentContext;
  /** Replaces the complete value of one namespace. */
  setContext: <Key extends keyof AgentContextValues>(
    key: Key,
    value: AgentContextValues[Key],
  ) => void;
  clearContext: (key: keyof AgentContextValues) => void;
};
export const AgentFocusContext = createContext<AgentFocusContextValue>({
  context: {},
  setContext: () => {},
  clearContext: () => {},
});

export function useAgentContext() {
  return useContext(AgentFocusContext);
}

/** Publishes one complete namespace while mounted; null or undefined clears it. */
export function usePublishAgentContext<Key extends keyof AgentContextValues>(
  key: Key,
  value: AgentContextValues[Key] | null | undefined,
): void {
  const { setContext, clearContext } = useAgentContext();
  const stableValue = useStableWhileEqual(value);
  useEffect(() => {
    if (stableValue == null) {
      return;
    }
    setContext(key, stableValue);
    return () => clearContext(key);
  }, [key, stableValue, setContext, clearContext]);
}

/** Serializes the shared snapshot for v1's focused-content readers. */
export function useAgentFocusedContent(): string {
  const { context } = useAgentContext();
  return formatV1FocusedContent(context);
}

/** The brand of the page the person is on, and which kind of page it is. */
export type PageBrand = {
  brandProfileId: string;
  page: "canvas" | "task" | "project";
};

/** The brand of the canvas, task, or project the person is on, which limits the composer's brand-specific options. */
export function usePageBrand(): PageBrand | undefined {
  const { context } = useAgentContext();
  // Canvas, task, and project pages are separate routes, so at most one publishes.
  if (context.canvas) {
    const { brandProfileId } = context.canvas;
    return brandProfileId ? { brandProfileId, page: "canvas" } : undefined;
  }
  if (context.task) {
    const { brandProfileId } = context.task;
    return brandProfileId ? { brandProfileId, page: "task" } : undefined;
  }
  if (context.project) {
    const { brandProfileId } = context.project;
    return brandProfileId ? { brandProfileId, page: "project" } : undefined;
  }
  return undefined;
}
